import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import CodeInput from "./CodeInput";
import { maskedLast4, toUsDigits } from "./phoneDigits";

const RULE = "Enter a 10-digit US mobile number.";

// docs/specs/login-setup.md §2, step "phone". Presentational: calls go out through the props (useLoginSetup).
// No consent box or text (A-1, Amar 2026-10-29: the customer already holds the SMS consent). The typed number lives
// in this component's state only, until it is sent.
const PhoneSetupStep = ({ phoneOnFile, sentTo, sendBlocked, busy, error, onSend, onConfirm, onChangeNumber }) => {
  const [useOnFile, setUseOnFile] = useState(Boolean(phoneOnFile));
  const [typed, setTyped] = useState("");
  const [code, setCode] = useState("");
  const onFile = Boolean(phoneOnFile) && useOnFile;
  const digits = toUsDigits(typed);
  const choice = onFile ? null : digits;
  const invalid = typed.trim() !== "" && !digits;
  const canSend = (onFile || Boolean(digits)) && !busy && !sendBlocked;

  const changeNumber = () => {
    setUseOnFile(false);
    setCode("");
    onChangeNumber();
  };

  const limitLine = sendBlocked && (
    <p className="mfa-resend text-muted small">No more codes can be sent for this sign-in.</p>
  );
  const errorLine = error && <div className="alert alert-error mt-2" role="alert">{error}</div>;

  if (sentTo) {
    return (
      <div>
        <p className="text-muted small mb-2">We texted a 6-digit code to {maskedLast4(sentTo)}.</p>
        <CodeInput method="sms" value={code} onChange={setCode} disabled={busy}
          onResend={sendBlocked ? undefined : () => onSend(choice)} />
        {limitLine}
        {errorLine}
        <Button className="w-100 mt-3 mb-2 text-white fw-bold" type="button" disabled={busy} onClick={() => onConfirm(code)}>
          Verify and Continue
        </Button>
        <Button variant="link" className="p-0" type="button" disabled={busy} onClick={changeNumber}>
          Use a different number
        </Button>
      </div>
    );
  }

  return (
    <div>
      <p className="mb-2">Confirm your mobile number. We&apos;ll text you a code.</p>
      {phoneOnFile && (
        <Form.Group className="mb-2">
          <Form.Check type="radio" id="mfa-phone-on-file" name="mfa-phone" label={`Text ${maskedLast4(phoneOnFile)}`}
            checked={onFile} disabled={busy} onChange={() => setUseOnFile(true)} />
          <Form.Check type="radio" id="mfa-phone-other" name="mfa-phone" label="Use a different number"
            checked={!onFile} disabled={busy} onChange={() => setUseOnFile(false)} />
        </Form.Group>
      )}
      {!onFile && (
        <Form.Group className="mb-2" controlId="mfa-phone-number">
          <Form.Label>Mobile number</Form.Label>
          <Form.Control type="tel" inputMode="tel" autoComplete="tel-national" value={typed} disabled={busy}
            maxLength={20} isInvalid={invalid} onChange={(e) => setTyped(e.target.value)} />
          {invalid && <Form.Control.Feedback type="invalid">{RULE}</Form.Control.Feedback>}
        </Form.Group>
      )}
      {limitLine}
      {errorLine}
      <Button className="w-100 mt-3 mb-2 text-white fw-bold" type="button" disabled={!canSend}
        onClick={() => canSend && onSend(choice)}>
        Send code
      </Button>
    </div>
  );
};

export default PhoneSetupStep;
